"use client";

import React, { useState, useMemo } from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { ArrowRight, ArrowUpDown, Info } from "lucide-react";
import { PORTFOLIO, COMPETITOR_NOTE, type PortfolioEntry } from "../data";
import { ixTrack } from "../analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";

/**
 * eSIM Index — the signature SORTABLE comparison table per DESIGN_INSTRUCTIONS.md.
 * Compares the portfolio eSIM EXPERIENCES by use-case with a transparent
 * "from" price + destination count drawn from the REAL shared catalog. The "From"
 * column shows each storefront's OWN price for the SAME Japan plan, computed via
 * the per-brand pricing engine (lib/brand-pricing.ts) — so the 8 rows show 8
 * honest, brand-specific prices off one backend. The destination count is shared
 * (one catalog). NO fabricated ratings/reviews.
 */
type SortKey = "rank" | "name" | "archetype";

export default function ComparisonTable({
  fromPrice,
  priceByRow,
  countryCount,
}: {
  fromPrice?: string;
  /** per-row "from" price for the same Japan plan, keyed by PortfolioEntry.key */
  priceByRow?: Record<string, string>;
  countryCount: number;
}) {
  const { t } = useTranslations();
  const [sort, setSort] = useState<SortKey>("rank");

  const applySort = (key: SortKey) => { setSort(key); ixTrack.tableSorted(key); };

  const rows = useMemo(() => {
    const r = PORTFOLIO.map((p, i) => ({ p, rank: i + 1 }));
    if (sort === "name") r.sort((a, b) => a.p.name.localeCompare(b.p.name));
    else if (sort === "archetype") r.sort((a, b) => a.p.archetype.localeCompare(b.p.archetype));
    else r.sort((a, b) => a.rank - b.rank);
    return r;
  }, [sort]);

  const th = (label: string, key?: SortKey) => (
    <th>
      {key ? (
        <button type="button" onClick={() => applySort(key)} className="inline-flex items-center gap-1 hover:text-primary">
          {label} <ArrowUpDown className="w-3 h-3" />
        </button>
      ) : label}
    </th>
  );

  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" className="flex items-end justify-between gap-4 mb-4" y={14}>
        <div>
          <h2 className="text-2xl sm:text-3xl">{t("ix.table.title", "Travel eSIM experiences compared")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("ix.table.sub", "Same trusted catalog and instant delivery — ranked by who each experience suits best.")}</p>
        </div>
      </Reveal>

      <Reveal as="div" className="ix-card overflow-x-auto" y={18}>
        <table className="ix-table">
          <thead>
            <tr>
              {th(t("ix.table.h0", "#"))}
              {th(t("ix.table.h1", "eSIM experience"), "name")}
              {th(t("ix.table.h2", "Type"), "archetype")}
              {th(t("ix.table.h3", "Best for"))}
              {th(t("ix.table.h4", "From"))}
              {th(t("ix.table.h5", "Destinations"))}
              {th("")}
            </tr>
          </thead>
          <Stagger as="tbody" staggerChildren={0.05} amount={0.1}>
            {rows.map(({ p, rank }: { p: PortfolioEntry; rank: number }) => (
              <StaggerItem as="tr" key={p.key} y={8}>
                <td><span className={`ix-rank ${rank === 2 ? "ix-rank-2" : rank === 3 ? "ix-rank-3" : ""}`}>{rank}</span></td>
                <td className="font-bold text-foreground">{p.name}</td>
                <td className="text-muted-foreground">{p.archetype}</td>
                <td><span className="ix-badge">{p.bestFor}</span></td>
                <td className="font-semibold text-foreground">{priceByRow?.[p.key] ?? fromPrice ?? "—"}</td>
                <td className="text-muted-foreground">{countryCount}+</td>
                <td><Link href={p.href} onClick={() => { ixTrack.providerRowClicked(p.key); ixTrack.outboundReferralClicked(p.key, p.bestFor); }} className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline whitespace-nowrap">{t("ix.table.view", "View")} <ArrowRight className="w-3.5 h-3.5" /></Link></td>
              </StaggerItem>
            ))}
          </Stagger>
        </table>
      </Reveal>

      <p className="mt-3 inline-flex items-start gap-2 text-xs text-muted-foreground max-w-3xl">
        <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        {t("ix.table.note", COMPETITOR_NOTE)}
      </p>
    </section>
  );
}
